const BASE = '/api';

async function request(path, { method = 'GET', body, signal } = {}) {
  const res = await fetch(BASE + path, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });

  const isJson = (res.headers.get('content-type') || '').includes('application/json');
  const data = isJson ? await res.json().catch(() => null) : null;

  if (!res.ok) {
    const err = new Error(data?.error || res.statusText || 'request_failed');
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
}

export const api = {
  auth: {
    register: (email, password) => request('/auth/register', { method: 'POST', body: { email, password } }),
    login: (email, password) => request('/auth/login', { method: 'POST', body: { email, password } }),
    loginPin: (challengeToken, pin) =>
      request('/auth/login/pin', { method: 'POST', body: { challengeToken, pin } }),
    waitlist: (payload) => request('/auth/waitlist', { method: 'POST', body: payload }),
    logout: () => request('/auth/logout', { method: 'POST' }),
    me: () => request('/auth/me'),
    typingProfile: () => request('/auth/typing-profile'),
    typingProfileSet: (optedOut) =>
      request('/auth/typing-profile', { method: 'PATCH', body: { optedOut } }),
    typingProfileAnnounce: () =>
      request('/auth/typing-profile/announce', { method: 'POST' }),
    setLevel: (level) => request('/auth/level', { method: 'PATCH', body: { level } }),
    verifyRequest: () => request('/auth/verify/request', { method: 'POST' }),
    verifyConfirm: (token) => request('/auth/verify/confirm', { method: 'POST', body: { token } }),
    changeEmail: (email) => request('/auth/email', { method: 'PATCH', body: { email } }),
    forgot: (email) => request('/auth/forgot', { method: 'POST', body: { email } }),
    reset: (token, password) => request('/auth/reset', { method: 'POST', body: { token, password } }),
  },
  support: {
    listMine: () => request('/support/tickets'),
    show: (id) => request('/support/tickets/' + encodeURIComponent(id)),
    create: (payload) => request('/support/tickets', { method: 'POST', body: payload }),
    addMessage: (id, message) =>
      request('/support/tickets/' + encodeURIComponent(id) + '/messages', {
        method: 'POST',
        body: { message },
      }),
  },
  adminSettings: {
    getIntegrations: () => request('/admin/settings/integrations'),
    updateIntegrations: (patch) =>
      request('/admin/settings/integrations', { method: 'PATCH', body: patch }),
    testTelegram: () =>
      request('/admin/settings/integrations/test', { method: 'POST' }),
    getLimits: () => request('/admin/settings/limits'),
    updateLimits: (patch) =>
      request('/admin/settings/limits', { method: 'PATCH', body: patch }),
    listWaitlist: (opts = {}) => {
      const qs = new URLSearchParams();
      if (opts.limit) qs.set('limit', String(opts.limit));
      if (opts.offset) qs.set('offset', String(opts.offset));
      const suffix = qs.toString() ? '?' + qs.toString() : '';
      return request('/admin/settings/waitlist' + suffix);
    },
    deleteWaitlist: (id) =>
      request('/admin/settings/waitlist/' + encodeURIComponent(id), { method: 'DELETE' }),
    triggerDailySummary: () =>
      request('/admin/settings/daily-summary/test', { method: 'POST' }),
    getTypingGate: () => request('/admin/settings/typing-gate'),
    updateTypingGate: (patch) =>
      request('/admin/settings/typing-gate', { method: 'PATCH', body: patch }),
  },
  adminSupport: {
    list: (opts = {}) => {
      const qs = new URLSearchParams();
      if (opts.status) qs.set('status', opts.status);
      if (opts.limit) qs.set('limit', String(opts.limit));
      if (opts.offset) qs.set('offset', String(opts.offset));
      const suffix = qs.toString() ? '?' + qs.toString() : '';
      return request('/admin/support/tickets' + suffix);
    },
    show: (id) => request('/admin/support/tickets/' + encodeURIComponent(id)),
    reply: (id, message) =>
      request('/admin/support/tickets/' + encodeURIComponent(id) + '/reply', {
        method: 'POST',
        body: { message },
      }),
    setStatus: (id, status) =>
      request('/admin/support/tickets/' + encodeURIComponent(id) + '/status', {
        method: 'PATCH',
        body: { status },
      }),
  },
  admin: {
    listUsers: (opts = {}) => {
      const qs = new URLSearchParams();
      if (opts.q) qs.set('q', opts.q);
      if (opts.limit) qs.set('limit', String(opts.limit));
      if (opts.offset) qs.set('offset', String(opts.offset));
      const suffix = qs.toString() ? '?' + qs.toString() : '';
      return request('/admin/users' + suffix);
    },
    showUser: (id) => request('/admin/users/' + encodeURIComponent(id)),
    updateUser: (id, patch) => request('/admin/users/' + encodeURIComponent(id), { method: 'PATCH', body: patch }),
    resetProgress: (id) => request('/admin/users/' + encodeURIComponent(id) + '/reset-progress', { method: 'POST' }),
    deleteUser: (id) => request('/admin/users/' + encodeURIComponent(id), { method: 'DELETE' }),
  },
  collocations: {
    next: () => request('/collocations/next'),
    drum: () => request('/collocations/drum'),
    byId: (id) => request('/collocations/' + encodeURIComponent(id)),
  },
  attempts: {
    submit: (payload) => request('/attempts', { method: 'POST', body: payload }),
  },
  progress: {
    stats: () => request('/progress/stats'),
    heatmap: (days = 28) => request('/progress/heatmap?days=' + days),
    mastered: (limit = 10) => request('/progress/mastered?limit=' + limit),
    skipToDrum: (progressId) =>
      request('/progress/' + encodeURIComponent(progressId) + '/skip-to-drum', { method: 'POST' }),
  },
  sessions: {
    start: () => request('/sessions', { method: 'POST' }),
    end: (id) => request('/sessions/' + id + '/end', { method: 'POST' }),
  },
  gamification: {
    dashboard: () => request('/gamification/dashboard'),
  },
  badges: {
    get: () => request('/badges'),
  },
};
